import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_URL } from "../../config/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import "../../styles/derma.css";

/**
 * Resit selepas bayaran, dan jalan kembali ke dalam aplikasi.
 *
 * ToyyibPay mengembalikan penyumbang ke sini selepas FPX selesai. Untuk pengguna
 * iOS yang datang daripada aplikasi, halaman ini ialah jambatan pulang.
 *
 *
 * STATUS DIAMBIL SEMULA, TIDAK DIPERCAYAI DARIPADA URL
 *
 * Parameter pulangan gerbang kelihatan dalam bar alamat dan boleh disunting
 * sesiapa. Memaparkan "Berjaya" kerana URL berkata begitu bermakna sesiapa boleh
 * menghasilkan resit yang meyakinkan. Kebenaran datang daripada pelayan, yang
 * membacanya daripada rekod bayaran.
 *
 *
 * DUA PAUTAN PULANG, BUKAN SATU
 *
 * Pautan universal (https://) ialah yang utama kerana ia berfungsi walaupun
 * aplikasi tidak dipasang — ia hanya kekal di web. Tetapi ia gagal SENYAP apabila
 * fail apple-app-site-association belum di-cache oleh peranti, dan penyumbang
 * yang baru sahaja membayar tidak boleh dibiarkan terkandas. Jadi skema tersuai
 * ditawarkan sebagai sandaran.
 */

const STATUS_BERJAYA = "1";
const STATUS_PENDING = "2";

function tarikhCantik(t) {
    if (!t) return null;
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("ms-MY", {
        day: "numeric", month: "long", year: "numeric",
        hour: "numeric", minute: "2-digit",
    });
}

function ringgit(n) {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : null;
}

const Resit = () => {
    const { orderId: orderIdParam } = useParams();
    const [searchParams] = useSearchParams();

    // ToyyibPay memulangkan order_id sebagai parameter pertanyaan, tetapi
    // aplikasi memaut ke sini dengan ia dalam laluan. Kedua-duanya diterima.
    const orderId = orderIdParam || searchParams.get("order_id") || searchParams.get("orderId");
    const statusUrl = searchParams.get("status_id") || searchParams.get("status");

    const [memuat, setMemuat] = useState(true);
    const [data, setData] = useState(null);
    const [ralat, setRalat] = useState(null);

    const asasWeb = useMemo(() => String(API_URL).replace(/\/api\/?$/, ""), []);

    useEffect(() => {
        let dibatalkan = false;

        const muat = async () => {
            if (!orderId) {
                setRalat("Nombor rujukan transaksi tidak diterima.");
                setMemuat(false);
                return;
            }
            try {
                const r = await fetch(
                    `${asasWeb}/web/derma/resit/${encodeURIComponent(orderId)}`
                );
                const json = await r.json();
                if (dibatalkan) return;

                if (json?.status === 200 && json?.data) setData(json.data);
                else setRalat(json?.message || "Resit tidak dijumpai.");
            } catch (e) {
                if (!dibatalkan) setRalat("Tidak dapat menghubungi pelayan.");
            } finally {
                if (!dibatalkan) setMemuat(false);
            }
        };

        muat();
        return () => {
            dibatalkan = true;
        };
    }, [asasWeb, orderId]);

    const statusAkhir = String(data?.status_kod ?? statusUrl ?? "");
    const berjaya = statusAkhir === STATUS_BERJAYA;
    const pending = statusAkhir === STATUS_PENDING;

    // Bentuk PERTANYAAN, bukan segmen laluan.
    //
    // Ini sepadan dengan bentuk yang ToyyibPay sendiri hasilkan semasa
    // mengembalikan penyumbang, dan aplikasi memetakan satu laluan sahaja untuk
    // kedua-duanya. Menggunakan bentuk berbeza di sini bermakna dua bentuk URL
    // perlu dikekalkan, dan yang jarang diuji akan senyap rosak.
    const pautanApp = orderId
        ? `aljariyah://derma/resit?order_id=${encodeURIComponent(orderId)}&status_id=${encodeURIComponent(statusAkhir || "")}`
        : null;

    if (memuat) {
        return (
            <div className="derma-root">
                <div className="flex min-h-screen items-center justify-center px-6">
                    <p className="text-base text-muted-foreground">Memuatkan resit…</p>
                </div>
            </div>
        );
    }

    const tajuk = ralat
        ? "Resit tidak dapat dipaparkan"
        : berjaya
        ? "Sumbangan Berjaya"
        : pending
        ? "Sumbangan Dalam Proses"
        : "Sumbangan Tidak Berjaya";

    const mesej = ralat
        ? ralat
        : berjaya
        ? "Terima kasih. Semoga Allah membalas kebaikan anda."
        : pending
        ? "Bayaran anda sedang diproses. Resit akan dikemas kini sebaik sahaja disahkan."
        : "Bayaran anda tidak dapat diselesaikan. Tiada jumlah telah ditolak.";

    const warna = ralat || (!berjaya && !pending) ? "text-destructive" : berjaya ? "text-primary" : "text-amber-600";
    const sempadan = ralat || (!berjaya && !pending) ? "border-destructive" : berjaya ? "border-primary" : "border-amber-500";

    return (
        <div className="derma-root">
            <div className="mx-auto max-w-md px-5 pb-16 pt-12">
                <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 ${sempadan}`}>
                    <span className={`text-2xl ${warna}`}>{berjaya ? "✓" : pending ? "…" : "!"}</span>
                </div>

                {/* div dan bukan h1 — App.scss menetapkan h1 { font-size: 55px !important }
                    dalam media query mudah alih, yang tiada kekhususan boleh atasi. */}
                <div role="heading" aria-level="1" className="mb-2 text-center text-2xl font-semibold leading-tight">
                    {tajuk}
                </div>
                <p className="mb-8 text-center text-base leading-relaxed text-muted-foreground">{mesej}</p>

                {data ? (
                    <>
                        <div className="mb-6 rounded-lg bg-primary/5 px-5 py-6 text-center">
                            <span className="block text-sm text-muted-foreground">Jumlah sumbangan</span>
                            <span className="mt-1 block text-3xl font-semibold text-primary">
                                RM {ringgit(data.amaun)}
                            </span>
                            {data.no_resit ? (
                                <span className="mt-1.5 block text-sm text-muted-foreground">
                                    Resit {data.no_resit}
                                </span>
                            ) : null}
                        </div>

                        <Seksyen tajuk="Maklumat Sumbangan">
                            <Baris label="Jenis" nilai={data.jenis} />
                            <Baris label="Tabung" nilai={data.tabung} />
                            <Baris label="Keterangan" nilai={data.keterangan} />
                            <Baris label="Tarikh" nilai={tarikhCantik(data.tarikh)} />
                            <Baris label="Status" nilai={data.status_label} />
                        </Seksyen>

                        <Seksyen tajuk="Maklumat Institusi">
                            <Baris label="Institusi" nilai={data.institusi} />
                            <Baris label="E-mel" nilai={data.institusi_emel} />
                            <Baris label="Telefon" nilai={data.institusi_telefon} />
                            {/*
                              Hanya dipaparkan apabila caj gerbang benar-benar
                              direkod. Sebahagian rekod menyimpan nilai bersih
                              sebagai sifar, yang bermaksud "tidak direkod" dan
                              BUKAN "institusi tidak menerima apa-apa".
                            */}
                            <Baris label="Diterima institusi" nilai={ringgit(data.bersih) ? `RM ${ringgit(data.bersih)}` : null} />
                            <Baris label="Caj pemprosesan" nilai={ringgit(data.caj) ? `RM ${ringgit(data.caj)}` : null} />
                        </Seksyen>

                        <Seksyen tajuk="Maklumat Penyumbang">
                            <Baris label="Nama" nilai={data.penyumbang} />
                            <Baris label="E-mel" nilai={data.emel} />
                            <Baris label="Telefon" nilai={data.telefon} />
                        </Seksyen>

                        <Seksyen tajuk="Maklumat Pembayaran">
                            <Baris label="No. Rujukan" nilai={data.no_rujukan} kecil />
                            <Baris label="Saluran" nilai={data.saluran} />
                            <Baris label="Kod Bil" nilai={data.kod_bil} />
                            <Baris label="Invois Gerbang" nilai={data.invois_gerbang} kecil />
                            <Baris label="ID FPX" nilai={data.fpx_id} kecil />
                        </Seksyen>

                        {berjaya ? (
                            <Button variant="outline" size="lg" className="mb-3 w-full" asChild>
                                <a href={`${asasWeb}/web/derma/resit/${encodeURIComponent(orderId)}/pdf`} target="_blank" rel="noreferrer">
                                    Muat Turun Resit PDF
                                </a>
                            </Button>
                        ) : null}
                    </>
                ) : null}

                {!data && !ralat ? (
                    <Alert className="mb-8" variant="info">
                        <AlertDescription>
                            Butiran resit belum tersedia. Ia akan dihantar ke e-mel anda sebaik sahaja bayaran disahkan.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {pautanApp ? (
                    <div className="space-y-3">
                        <Button size="lg" className="w-full" asChild>
                            <a href={pautanApp}>Kembali ke Aplikasi</a>
                        </Button>
                        <p className="text-center text-sm text-muted-foreground">
                            Jika aplikasi tidak terbuka, anda boleh menutup tetingkap ini dengan selamat —
                            sumbangan anda telah direkodkan.
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

// Seksyen yang setiap barisnya kosong tidak dirender langsung — kepala seksyen
// yang berdiri sendiri di atas ruang kosong kelihatan seperti data yang gagal
// dimuat.
const Seksyen = ({ tajuk, children }) => {
    const isi = React.Children.toArray(children).filter(Boolean);
    const adaIsi = isi.some((c) => c && c.props && c.props.nilai);
    if (!adaIsi) return null;

    return (
        <div className="mb-6">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {tajuk}
            </span>
            <Card>
                <CardContent className="px-4 py-1">{children}</CardContent>
            </Card>
        </div>
    );
};

// Baris kosong disembunyikan sepenuhnya. Resit yang penuh dengan sengkang
// kelihatan seperti rekod yang rosak kepada penyumbang.
const Baris = ({ label, nilai, kecil }) => {
    if (nilai === null || nilai === undefined || nilai === "") return null;
    return (
        <div className="flex items-start justify-between gap-6 border-b border-border py-3 last:border-0">
            <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
            <span className={`text-right font-medium ${kecil ? "text-xs" : "text-sm"}`}>{String(nilai)}</span>
        </div>
    );
};

export default Resit;
